/**
 * Authenticated fetch wrapper for Algoglide backend API calls.
 *
 * Client-side usage (React components):
 *   import { authFetch } from '@/lib/auth-fetch';
 *   const res = await authFetch(`${BACKEND_URL}/api/...`);
 *   // Token is retrieved automatically from Privy's client store.
 *
 * Server-side usage (Next.js API routes — token must be threaded explicitly):
 *   const res = await authFetch(`${BACKEND_URL}/api/...`, options, token);
 *   // Pass the Privy token read from the incoming request headers.
 */

import { getAccessToken } from '@privy-io/react-auth';

/**
 * Fetch wrapper that injects the Privy access token as 'x-privy-access-token'.
 * @param token - Optional explicit token (for server-side callers that can't use hooks).
 *                When omitted, retrieves token from Privy's client store (client-side only).
 */
export async function authFetch(
  url: string,
  options?: RequestInit,
  token?: string,
): Promise<Response> {
  let accessToken = token;

  if (!accessToken) {
    try {
      accessToken = await getAccessToken() ?? undefined;
    } catch {
      // Not in a Privy context (e.g., server-side without explicit token) — fall through
    }
  }

  if (accessToken) {
    const existingHeaders = (options?.headers as Record<string, string>) || {};
    return fetch(url, {
      ...options,
      headers: {
        ...existingHeaders,
        'x-privy-access-token': accessToken,
      },
    });
  }

  return fetch(url, options);
}
